import { useMemo } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { blobURL } from '../../common/StringUtils';

interface UseExpressPaymentValidationProps {
  isLoginWithBankId: number;
  selectedCustomer: any;
  calculatedSubAmount: any;
  t: (key: string) => string;
  setValue: UseFormSetValue<any>;
  setIsExpressCheckboxChecked: React.Dispatch<React.SetStateAction<boolean>>;
  setShowExpressPaymentModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowExpressValidMessage: React.Dispatch<React.SetStateAction<boolean>>;
  setExpressPayments: React.Dispatch<React.SetStateAction<boolean>>;
  handleInputChange: (field: string, value: any) => void;
  // Drives the requirements modal's footer: 'info' (Read more → no buttons) vs
  // 'confirm' (toggle → Accept / Reject).
  setExpressModalMode: React.Dispatch<React.SetStateAction<'info' | 'confirm'>>;
}

/**
 * Custom hook to handle Express Payment validation and related functions
 * Provides validation logic, message generation, and handlers for express payment checkbox
 * @param props - Configuration object containing customer data, translations, and state setters
 * @returns Object containing express payment functions and computed values
 */
export const useExpressPaymentValidation = ({
  isLoginWithBankId,
  selectedCustomer,
  calculatedSubAmount,
  t,
  setValue,
  setIsExpressCheckboxChecked,
  setShowExpressPaymentModal,
  setShowExpressValidMessage,
  setExpressPayments,
  handleInputChange,
  setExpressModalMode,
}: UseExpressPaymentValidationProps) => {
  
  // Express payment logo URLs based on language
  const language = localStorage.getItem('i18nextLng');
  const expressDe = `${blobURL}/express-logo-de.png`;
  const expressEng = `${blobURL}/express-logo-eng.png`;

  // Get appropriate error message for express payment validation
  const getExpressPaymentMessage = useMemo(() => {
    return () => {
      if (isLoginWithBankId !== 1) {
       return t('EXPRESS_PAYMENT_LOGIN_BANKID');
      } else if (selectedCustomer?.paymentTerm === '45' || selectedCustomer?.paymentTerm === '60') {
        return t("We don't accept express payment for 45 & 60 days.");
      } else if (selectedCustomer?.accountType === 1) {
        return t("We don't accept express payment for private customers.");
      } else if (selectedCustomer?.country !== 'Sweden' && selectedCustomer?.accountType !== 1) {
        return t("We don't accept express payments for customers outside Sweden.");
      } else if (calculatedSubAmount?.response?.totalCalculation?.amountIncludingVat > 60000) {
        return t('For Express Payment, there is a limit of SEK 60,000 per invoice.');
      }
      return '';
    };
  }, [isLoginWithBankId, selectedCustomer, calculatedSubAmount, t]);

  // Check if Express Payment is available (all conditions must be met)
  const isExpressPaymentAvailable = useMemo(() => {
    return () => {
      const available = (
        isLoginWithBankId === 1 &&
        selectedCustomer?.paymentTerm !== '45' &&
        selectedCustomer?.paymentTerm !== '60' &&
        selectedCustomer?.accountType !== 1 &&
        selectedCustomer?.country === 'Sweden' &&
        calculatedSubAmount?.response?.totalCalculation?.amountIncludingVat <= 60000
      );
      
      // Debug console logs
     return available;
    };
  }, [isLoginWithBankId, selectedCustomer, calculatedSubAmount]);

  // Check if warning message should be shown
  const shouldShowWarningMessage = useMemo(() => {
    return (isCheckboxChecked: boolean) => {
      return isCheckboxChecked && (
        isLoginWithBankId !== 1 ||
        selectedCustomer?.paymentTerm === '45' ||
        selectedCustomer?.paymentTerm === '60' ||
        selectedCustomer?.accountType === 1 ||
        (selectedCustomer?.country !== 'Sweden' && selectedCustomer?.accountType !== 1) ||
        calculatedSubAmount?.response?.totalCalculation?.amountIncludingVat > 60000
      );
    };
  }, [isLoginWithBankId, selectedCustomer, calculatedSubAmount]);

  // Handle express payment checkbox change
  const handleExpressPaymentCheckbox = (isChecked: boolean) => {
    // Check if Express Payment is available
    if (!isExpressPaymentAvailable()) {
      // Prevent checkbox from being checked if validation fails
      setValue('expressPayout', false);
      setIsExpressCheckboxChecked(false);
      return;
    }
    
    setIsExpressCheckboxChecked(isChecked);

    if (isChecked) {
      // All requirements met - open modal in confirm mode (Accept / Reject)
      setExpressModalMode('confirm');
      setShowExpressPaymentModal(true);
      setValue('expressPayout', true);
      setShowExpressValidMessage(false);
    } else {
      // User unchecked - clear everything
      setShowExpressPaymentModal(false);
      setShowExpressValidMessage(false);
      setValue('expressPayout', false);
    }
  };

  // Handle "Read more" link click
  const handleExpressPaymentReadMore = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    // Read more is purely informational — open in 'info' mode (no action buttons).
    setExpressModalMode('info');
    setShowExpressPaymentModal(true);
  };

  // User manually UNCHECKING an already-enabled Express Payment. Instead of
  // clearing it instantly, open the same confirm modal first — the user then
  // decides: Accept (keep it on) or Reject (turn it off). We don't touch the
  // form value here, so the checkbox stays checked until the choice is made.
  // (Programmatic auto-unchecks still go through handleExpressPaymentCheckbox,
  //  which stays silent — no modal.)
  const handleExpressPaymentUncheckRequest = () => {
    setExpressModalMode('confirm');
    setShowExpressPaymentModal(true);
  };

  // Handle "Reject" in the confirm modal — revert the toggle so Express Payment
  // stays unchecked, then the modal closes.
  const handleExpressPaymentModalReject = () => {
    setValue('expressPayout', false);
    setExpressPayments(false);
    setIsExpressCheckboxChecked(false);
    setShowExpressValidMessage(false);
    handleInputChange('expressPayout', false);
  };

  // Handle express payment modal confirmation ("Got it")
  const handleExpressPaymentModalConfirm = () => {
    // This same requirements modal is also opened from the "Read more" link, so
    // clicking "Got it" must NEVER enable Express Payment unless every
    // requirement is met (BankID login, customer eligibility, amount limit, …).
    // Run exactly the same validation as the checkbox.
    if (!isExpressPaymentAvailable()) {
      setValue('expressPayout', false);
      setExpressPayments(false);
      setIsExpressCheckboxChecked(false);
      handleInputChange('expressPayout', false);
      return;
    }

    setValue('expressPayout', true);
    setExpressPayments(true);
    handleInputChange('expressPayout', true);
  };

  return {
    language,
    expressDe,
    expressEng,
    getExpressPaymentMessage,
    isExpressPaymentAvailable,
    shouldShowWarningMessage,
    handleExpressPaymentCheckbox,
    handleExpressPaymentReadMore,
    handleExpressPaymentModalConfirm,
    handleExpressPaymentModalReject,
    handleExpressPaymentUncheckRequest,
  };
};
